import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDraft } from '../contexts/DraftContext';
import { useAuth } from '../contexts/AuthContext';
import {
  getLeagueBySourceDraft,
  createLeagueFromDraft,
  suggestDraftPick,
  getDraftRoundPrediction,
  getDraftGradesStatus,
  startDraftGrades,
  getAiUsage,
  getDraftGuestToken,
  clearDraftGuestToken,
} from '../services/api';
import { getAiErrorMessage } from '../utils/aiErrors';
import AiButton from './AiButton';
import AiSuggestionPanel from './AiSuggestionPanel';
import DraftGrades from './DraftGrades';
import PickTimer from './PickTimer';

function calculateTeamIndex(draftType, teamCount, round, pickNumber) {
  if (draftType?.toLowerCase() === 'snake' && round % 2 === 0) {
    return teamCount - pickNumber;
  }
  return pickNumber - 1;
}

/**
 * Top bar showing who is currently picking, a progress bar,
 * and action buttons (undo, leave draft, create league).
 */
function DraftControls() {
  const { draft, picks, draftTeams, isOwner, mySlotIndex, undoPick, clearDraft, pickLoading, skipExpiredPick, extendTimer, saveGuestDraftToAccount } = useDraft();
  const { user } = useAuth();
  const isPro = user?.tier === 'pro';
  // AI features require a real Cognito account server-side (an anonymous Commissioner Mode guest
  // has none), but no longer require being the commissioner specifically -- any logged-in
  // participant (member or commissioner) can use D1/D3/G1 for their own turn/draft.
  const isLoggedInParticipant = !!user;
  const navigate = useNavigate();

  const [checkingLeague, setCheckingLeague] = useState(true);
  const [existingLeague, setExistingLeague] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [leagueName, setLeagueName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  // AI state — D1 (Pick Suggestion)
  const [suggestion, setSuggestion] = useState(null);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestError, setSuggestError] = useState(null);
  // AI state — D3 (Round Prediction)
  const [roundPrediction, setRoundPrediction] = useState(null);
  const [predictionLoading, setPredictionLoading] = useState(false);
  const [predictionError, setPredictionError] = useState(null);
  const [lastRoundSeen, setLastRoundSeen] = useState(null);
  const [aiUsageRemaining, setAiUsageRemaining] = useState(20);
  // AI state — G1 (Post-Draft Grades). Async: gradesStatus is server-verified truth (not just
  // "is a local fetch in flight"), so a mid-generation page refresh restores the disabled/polling
  // button state instead of defaulting to a fresh, clickable one -- see the two effects below.
  const [grades, setGrades] = useState(null);
  const [gradesStatus, setGradesStatus] = useState('not_started'); // not_started|processing|complete|failed
  const [checkingGrades, setCheckingGrades] = useState(true);
  const [gradesError, setGradesError] = useState(null);
  const isCompleted = draft?.status === 'completed';
  // "Save to My Drafts" (guest -> account linking, completed drafts only -- see DraftService
  // .saveGuestDraftToAccount). hasGuestToken reads sessionStorage directly rather than context
  // state; it only needs to be right at render time for a completed draft, not reactive.
  const hasGuestToken = !!(draft && getDraftGuestToken(draft.draftId));
  // Create League eligibility: classic Draft Mode only ever has one participant (the owner), same
  // as before. Commissioner Mode has no single "owner slot" (see DraftService.createDraft) -- any
  // account-linked participant (a joined member, or a guest who has since used "Save to My
  // Drafts") is eligible to convert their OWN slot's picks into their own league, mirroring
  // LeagueService.createLeagueFromDraft's server-side resolution by memberUserId. mySlotIndex
  // updates immediately after a successful save (see DraftContext's saveGuestDraftToAccount).
  const hasClaimedSlot = draft?.commissionerMode ? mySlotIndex != null : isOwner;
  const [savingToAccount, setSavingToAccount] = useState(false);
  const [savedToAccount, setSavedToAccount] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    setSuggestion(null);
    setSuggestError(null);
  }, [picks.length]);

  useEffect(() => {
    if (!isPro) return;
    getAiUsage().then(data => setAiUsageRemaining(data.remaining)).catch(() => {});
  }, [isPro]);

  useEffect(() => {
    if (!isCompleted || !hasClaimedSlot || !draft || draft.mockDraft) return;
    setCheckingLeague(true);
    // Scoped to (userId, draftId) server-side (LeagueService.findLeagueBySourceDraft), so in
    // Commissioner Mode this correctly answers "does *this* participant already have their own
    // league from this shared draft", not "does the draft have a league" -- multiple participants
    // can each have their own.
    getLeagueBySourceDraft(draft.draftId)
      .then(league => setExistingLeague(league || null))
      .catch(err => {
        setExistingLeague(null);
        if (err.response?.status !== 404) {
          console.error('Failed to check for existing league:', err);
        }
      })
      .finally(() => setCheckingLeague(false));
  }, [isCompleted, hasClaimedSlot, draft]);

  // G1 mount-time restore: ask the server for the current grade-generation status BEFORE the
  // button can be interacted with -- this is what keeps the button disabled/showing "processing"
  // across a page refresh instead of defaulting to a fresh, clickable state.
  useEffect(() => {
    if (!isCompleted || !isLoggedInParticipant || !draft || draft.mockDraft) return;
    let cancelled = false;
    setCheckingGrades(true);
    getDraftGradesStatus(draft.draftId)
      .then(data => {
        if (cancelled) return;
        setGradesStatus(data.status);
        setAiUsageRemaining(data.usageRemaining);
        if (data.status === 'complete') setGrades(data);
        if (data.status === 'failed') setGradesError(data.error || 'Could not generate draft grades. Please try again.');
      })
      .catch(err => {
        if (!cancelled) console.error('Failed to check draft grades status:', err);
      })
      .finally(() => {
        if (!cancelled) setCheckingGrades(false);
      });
    return () => { cancelled = true; };
  }, [isCompleted, isLoggedInParticipant, draft]);

  // G1 poll loop: runs whenever gradesStatus transitions to 'processing', from either the
  // mount-time restore above or handleGetDraftGrades below -- self-reschedules internally rather
  // than relying on the effect re-running, since repeatedly setting the same 'processing' value
  // would not otherwise retrigger this effect.
  useEffect(() => {
    if (gradesStatus !== 'processing' || !draft) return;
    let cancelled = false;
    let timer = null;

    function poll() {
      getDraftGradesStatus(draft.draftId)
        .then(data => {
          if (cancelled) return;
          setGradesStatus(data.status);
          setAiUsageRemaining(data.usageRemaining);
          if (data.status === 'complete') {
            setGrades(data);
          } else if (data.status === 'failed') {
            setGradesError(data.error || 'Could not generate draft grades. Please try again.');
          } else if (data.status === 'processing') {
            timer = setTimeout(poll, 4000);
          }
        })
        .catch(err => {
          if (!cancelled) console.error('Failed to poll draft grades status:', err);
        });
    }

    timer = setTimeout(poll, 4000);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [gradesStatus, draft]);

  if (!draft) return null;

  const { teamCount, draftType, currentRound, currentPick, rounds } = draft;
  const ownerSlotIndex = draftTeams.find(t => t.isOwnerSlot)?.slotIndex ?? 0;
  const totalPicks = rounds * teamCount;
  const picksMade = picks.length;

  const currentTeamIndex = !isCompleted
    ? calculateTeamIndex(draftType, teamCount, currentRound, currentPick)
    : -1;
  const currentTeamName = currentTeamIndex >= 0
    ? (draftTeams.find(t => t.slotIndex === currentTeamIndex)?.teamName ?? `Team ${currentTeamIndex + 1}`)
    : '';
  // Commissioner Mode: "my turn" means the on-clock slot is mine (owner/member/guest alike), not
  // necessarily the draft's Cognito owner -- classic drafts keep the original owner-only check,
  // completely unchanged.
  const isUserTurn = !isCompleted
    && (draft.commissionerMode ? currentTeamIndex === mySlotIndex : currentTeamIndex === ownerSlotIndex);
  const isNewRound = !isCompleted && !draft?.mockDraft && currentRound !== lastRoundSeen && currentRound >= 2;

  async function handleSuggestPick() {
    setSuggestion(null);
    setSuggestError(null);
    setSuggestLoading(true);
    try {
      const data = await suggestDraftPick(draft.draftId);
      setSuggestion(data);
      setAiUsageRemaining(data.usageRemaining);
    } catch (err) {
      console.error('AI suggest pick failed:', err);
      setSuggestError(getAiErrorMessage(err, 'AI pick suggestion failed. Please try again.'));
    } finally {
      setSuggestLoading(false);
    }
  }

  async function handleRoundPrediction() {
    setRoundPrediction(null);
    setPredictionError(null);
    setPredictionLoading(true);
    setLastRoundSeen(currentRound);
    try {
      const data = await getDraftRoundPrediction(draft.draftId);
      setRoundPrediction(data);
      setAiUsageRemaining(data.usageRemaining);
    } catch (err) {
      console.error('AI round prediction failed:', err);
      setPredictionError(getAiErrorMessage(err, 'AI round prediction failed. Please try again.'));
    } finally {
      setPredictionLoading(false);
    }
  }

  async function handleGetDraftGrades() {
    setGradesError(null);
    try {
      // Idempotent on the backend: if a generation is already in flight this just returns its
      // current "processing" status again rather than starting a duplicate call. 'processing' is
      // picked up by the poll effect once gradesStatus updates below.
      const data = await startDraftGrades(draft.draftId);
      setGradesStatus(data.status);
      setAiUsageRemaining(data.usageRemaining);
      if (data.status === 'complete') {
        setGrades(data);
      } else if (data.status === 'failed') {
        setGradesError(data.error || 'Could not generate draft grades. Please try again.');
      }
    } catch (err) {
      console.error('AI draft grades failed:', err);
      setGradesError(getAiErrorMessage(err, 'Could not generate draft grades. Please try again.'));
    }
  }

  function handleLeave() {
    clearDraft();
    navigate('/profile');
  }

  async function handleSaveToAccount() {
    setSavingToAccount(true);
    setSaveError(null);
    try {
      await saveGuestDraftToAccount();
      clearDraftGuestToken(draft.draftId);
      setSavedToAccount(true);
    } catch (err) {
      setSaveError(err.response?.data?.error?.message || 'Failed to save this draft to your account');
    } finally {
      setSavingToAccount(false);
    }
  }

  /** Wraps a value in double-quotes, escaping internal quotes -- prevents CSV formula injection. */
  function csvQuote(value) {
    const str = value == null ? '' : String(value);
    return '"' + str.replace(/"/g, '""') + '"';
  }

  function handleExportCsv() {
    const sorted = [...picks].sort((a, b) => (a.overallPick ?? 0) - (b.overallPick ?? 0));
    const headers = ['Round', 'Pick', 'Overall', 'Team', 'Player', 'Position', 'NFL Team'];
    const rows = sorted.map(p => {
      const teamName = draftTeams.find(t => t.slotIndex === p.slotIndex)?.teamName ?? `Team ${p.slotIndex + 1}`;
      return [p.round, p.pickNumber, p.overallPick, csvQuote(teamName), csvQuote(p.playerName), csvQuote(p.position), csvQuote(p.team)].join(',');
    });
    const csv = headers.join(',') + '\n' + rows.join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${draft.draftName || 'draft'}-picks.csv`.replace(/[^\w.-]+/g, '_');
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleCreateLeague() {
    setCreating(true);
    setCreateError(null);
    try {
      const result = await createLeagueFromDraft(draft.draftId, {
        leagueName,
        sport: draft.sport,
        seasonYear: new Date().getFullYear(),
      });
      navigate(`/league/${result.leagueId}`);
    } catch (err) {
      setCreateError(err.response?.data?.error?.message || 'Failed to create league');
      setCreating(false);
    }
  }

  const createLeagueButton = isCompleted && hasClaimedSlot && !showPrompt && !draft.mockDraft && (
    existingLeague ? (
      <button
        className='btn btn-primary'
        disabled
        style={{ opacity: 0.5, cursor: 'not-allowed' }}
        title={`League '${existingLeague.leagueName}' already created from this draft`}
      >
        Create League
      </button>
    ) : (
      <button
        className='btn btn-primary'
        disabled={checkingLeague || creating}
        onClick={() => {
          setLeagueName(draft.draftName + ' League');
          setShowPrompt(true);
        }}
      >
        {creating ? 'Creating…' : 'Create League'}
      </button>
    )
  );

  return (
    <div className='draft-controls'>
      {draft.mockDraft && (
        <div className='mock-draft-banner'>
          Practice Mode — AI-simulated picks, not a real draft
        </div>
      )}
      <div className='controls-top'>
        {isCompleted ? (
          <div className='controls-status completed'>Draft Complete!</div>
        ) : (
          <div className={`controls-status ${isUserTurn ? 'your-turn' : ''}`}>
            <span className='controls-label'>Now picking:</span>
            <strong>{currentTeamName}</strong>
            <span className='controls-meta'>Round {currentRound} · Pick {currentPick} of {teamCount}</span>
            {isUserTurn && <span className='your-turn-badge'>YOUR TURN</span>}
            {draft.commissionerMode && (
              <PickTimer
                timeRemainingSeconds={draft.timeRemainingSeconds}
                isCommissioner={isOwner}
                onSkip={skipExpiredPick}
                onExtend={extendTimer}
              />
            )}
          </div>
        )}
        <div className='controls-actions'>
          {isOwner && !isCompleted && picks.length > 0 && (
            <button
              className='btn btn-secondary'
              onClick={undoPick}
              disabled={pickLoading}
            >
              Undo Pick
            </button>
          )}
          {/* AI features require a real Cognito account server-side -- an anonymous Commissioner
              Mode guest has none, so these stay gated on isLoggedInParticipant to avoid showing a
              button that can only fail when clicked. No longer commissioner-only: any logged-in
              participant can get suggestions for their own turn. Always true anyway for classic
              (non-commissioner-mode) drafts, since the owner is the only possible logged-in caller
              there. */}
          {isUserTurn && isLoggedInParticipant && (
            <AiButton
              label='Ask the Doctor for a Pick'
              isPro={isPro}
              usageRemaining={suggestion ? suggestion.usageRemaining : aiUsageRemaining}
              freeTooltip='Upgrade to Pro for AI pick suggestions'
              onClick={handleSuggestPick}
              loading={suggestLoading}
            />
          )}
          {(isNewRound || predictionLoading) && isLoggedInParticipant && (
            <AiButton
              label='Ask the Doctor to Predict a round'
              isPro={isPro}
              usageRemaining={roundPrediction ? roundPrediction.usageRemaining : aiUsageRemaining}
              freeTooltip='Upgrade to Pro for round predictions'
              onClick={handleRoundPrediction}
              loading={predictionLoading}
            />
          )}
          {isCompleted && isLoggedInParticipant && !draft.mockDraft && (
            <AiButton
              label='Ask the Doctor to Grade this Draft'
              isPro={isPro}
              usageRemaining={grades ? grades.usageRemaining : aiUsageRemaining}
              freeTooltip='Upgrade to Pro for AI-generated draft grades'
              onClick={handleGetDraftGrades}
              loading={checkingGrades || gradesStatus === 'processing'}
            />
          )}
          {createLeagueButton}
          {picks.length > 0 && (
            <button className='btn btn-secondary' onClick={handleExportCsv}>
              Export CSV
            </button>
          )}
          {/* A guest's picks already work fine within their own browser session (the stored guest
              token authorizes them regardless of login state) -- these two actions are about
              permanently attaching that slot to a real account, not about the draft "working." */}
          {isCompleted && draft.commissionerMode && hasGuestToken && !isLoggedInParticipant && (
            <button
              className='btn btn-secondary'
              onClick={() => navigate(`/login?returnTo=/draft/${draft.draftId}`)}
            >
              Sign In to Save This Draft
            </button>
          )}
          {isCompleted && draft.commissionerMode && hasGuestToken && isLoggedInParticipant && !savedToAccount && (
            <button
              className='btn btn-secondary'
              onClick={handleSaveToAccount}
              disabled={savingToAccount}
            >
              {savingToAccount ? 'Saving…' : 'Save to My Drafts'}
            </button>
          )}
          {savedToAccount && (
            <span style={{ color: 'var(--success)', fontSize: '0.85rem', alignSelf: 'center' }}>
              Saved to your account ✓
            </span>
          )}
          <button className='btn btn-secondary' onClick={handleLeave}>
            {isCompleted ? 'View Profile' : 'Leave Draft'}
          </button>
        </div>
      </div>

      {suggestError && (
        <div className='error-banner' style={{ margin: '8px 0' }}>{suggestError}</div>
      )}
      {predictionError && (
        <div className='error-banner' style={{ margin: '8px 0' }}>{predictionError}</div>
      )}
      {gradesError && (
        <div className='error-banner' style={{ margin: '8px 0' }}>{gradesError}</div>
      )}
      {!checkingGrades && gradesStatus === 'processing' && (
        <div className='grades-processing-hint' style={{ margin: '8px 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          Grading a full draft board can take a couple of minutes, especially for larger leagues — feel free to leave this screen and check back.
        </div>
      )}
      {saveError && (
        <div className='error-banner' style={{ margin: '8px 0' }}>{saveError}</div>
      )}
      {suggestion && (
        <AiSuggestionPanel
          title='AI Pick Suggestion'
          items={suggestion.suggestions}
          renderItem={item => ({
            primary: `${item.playerName} — ${item.position}, ${item.team}`,
            secondary: item.rationale,
            confidence: item.confidence,
          })}
          dataAsOf={suggestion.dataAsOf}
          onClose={() => setSuggestion(null)}
        />
      )}

      {roundPrediction && (
        <AiSuggestionPanel
          title={`Round ${currentRound} Predictions`}
          items={roundPrediction.predictions}
          renderItem={item => ({
            primary: `Pick ${item.pickNumber}: ${item.predictedPlayer} (${item.position}) — ${item.teamName}`,
            secondary: item.reasoning || null,
            confidence: item.confidence,
          })}
          dataAsOf={roundPrediction.dataAsOf}
          onClose={() => setRoundPrediction(null)}
        />
      )}

      {grades && (
        <DraftGrades
          teams={grades.teams}
          overallSummary={grades.overallSummary}
          dataAsOf={grades.dataAsOf}
          onClose={() => setGrades(null)}
        />
      )}

      {showPrompt && (
        <div className='create-league-prompt'>
          <input
            className='input'
            type='text'
            value={leagueName}
            onChange={e => setLeagueName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreateLeague()}
            placeholder='League name'
          />
          <button
            className='btn btn-primary'
            onClick={handleCreateLeague}
            disabled={creating || !leagueName.trim()}
          >
            {creating ? 'Creating…' : 'Confirm'}
          </button>
          <button
            className='btn btn-secondary'
            onClick={() => { setShowPrompt(false); setCreateError(null); }}
            disabled={creating}
          >
            Cancel
          </button>
          {createError && <span className='create-league-error'>{createError}</span>}
        </div>
      )}

      <div className='controls-progress'>
        <div
          className='controls-progress-bar'
          style={{ width: `${(picksMade / totalPicks) * 100}%` }}
        />
      </div>
      <div className='controls-progress-label'>
        {picksMade} / {totalPicks} picks
      </div>

    </div>
  );
}

export default DraftControls;
